/**
 * LECTURA DEL FRENTE, por geometría.
 *
 * Responsabilidad única: dadas las palabras que vio el OCR con sus cajas,
 * devolver qué dice el frente del documento. No sabe quién es el titular, no
 * compara con nada, no decide si algo está verificado. Solo lee.
 *
 * La regla del frente es una y no se negocia: **el valor está DEBAJO de su
 * rótulo, en su misma columna**. El frente es una grilla, y en orden de lectura
 * entre "Sexo" y su "M" se cruzan cinco palabras de las columnas vecinas.
 * Cualquier criterio de cercanía en el texto falla.
 *
 * El frente NO tiene dígitos de control. Todo lo que sale de acá es lectura
 * única, y así hay que tratarla.
 */
import { PalabraOcr } from "../dniMatch";
import { Hallazgo } from "./hallazgos";
import { Rectangulo } from "./types";
import { distancia, textoLimpio } from "./texto";

// ---------------------------------------------------------------------------
// 1. Utilidades de texto
// ---------------------------------------------------------------------------

const norm = (t: string) =>
  t.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/[^A-Z]/g, "");

const alto = (w: PalabraOcr) => Math.max(1, w.bbox.y1 - w.bbox.y0);

/** La caja que contiene a todas: es la que se señala en la foto. */
function unir(ws: PalabraOcr[]): Rectangulo {
  const x = Math.min(...ws.map((w) => w.bbox.x0));
  const y = Math.min(...ws.map((w) => w.bbox.y0));
  return {
    x,
    y,
    w: Math.max(...ws.map((w) => w.bbox.x1)) - x,
    h: Math.max(...ws.map((w) => w.bbox.y1)) - y,
  };
}

// ---------------------------------------------------------------------------
// 2. Filas
// ---------------------------------------------------------------------------

/**
 * Agrupa las palabras en filas por su LÍNEA DE BASE.
 *
 * El borde de ARRIBA no sirve para esto y fue la causa de un error caro. En la
 * grilla del documento el rótulo está impreso chico y su valor grande, así que
 * el borde de arriba del valor queda a la altura del rótulo de arriba, y los
 * dos terminaban en la misma fila. Medido sobre el frente de 2026 en el
 * navegador, el renglón salía así:
 *
 *     Noo | MARIANA | bees | Nama | FLORENCIA
 *
 * —el rótulo "Nombre/Name" partido en tres y el valor intercalado—. Y como una
 * palabra que vive en una fila de rótulos no puede ser un valor, el nombre no
 * solo no se encontraba: quedaba descartado.
 *
 * El borde de ABAJO es la línea de base, que es lo que comparten las palabras
 * de un renglón sin importar el cuerpo de la letra. La tolerancia sale de la
 * altura de la palabra candidata, que es la que puede tener descendentes.
 */
export function filas(palabras: PalabraOcr[]): PalabraOcr[][] {
  const orden = [...palabras].sort((a, b) => a.bbox.y1 - b.bbox.y1 || a.bbox.x0 - b.bbox.x0);
  const filas: PalabraOcr[][] = [];
  for (const w of orden) {
    const fila = filas.find(
      (f) => Math.abs(baseDe(f) - w.bbox.y1) < Math.max(alto(w), alto(f[0])) * TOLERANCIA_DE_BASE
    );
    if (fila) fila.push(w);
    else filas.push([w]);
  }
  for (const f of filas) f.sort((a, b) => a.bbox.x0 - b.bbox.x0);
  return filas;
}

/**
 * Cuánto puede alejarse de la base de su fila una palabra que igual le
 * pertenece. Una "y" o una "p" bajan el borde alrededor de un cuarto del
 * cuerpo; el salto al renglón siguiente es de uno entero.
 */
const TOLERANCIA_DE_BASE = 0.4;

/** La base de una fila: la mediana, para que una sola palabra rara no la mueva. */
function baseDe(fila: PalabraOcr[]): number {
  const bases = fila.map((w) => w.bbox.y1).sort((a, b) => a - b);
  return bases[Math.floor(bases.length / 2)];
}

// ---------------------------------------------------------------------------
// 3. Rótulos
// ---------------------------------------------------------------------------

/**
 * Los rótulos del frente, en un solo lugar.
 *
 * Se reconocen por PALABRA CLAVE sobre el texto de la fila entera, no por la
 * frase completa ni por palabras seguidas. Medido sobre una foto real: "Fecha de
 * nacimiento / Date of birth" se leyó como "Fecha" y "birth", sin nada en el
 * medio. Pidiendo la frase no se reconoce nunca; mirando la fila, sí.
 *
 * Y hay tres rótulos que empiezan con "Fecha" —nacimiento, emisión y
 * vencimiento—, así que lo que los distingue es su palabra única.
 */
/**
 * ¿Esta palabra del OCR es este rótulo?
 *
 * Renunciar a un dato sustancial porque al OCR le faltó una letra es el error
 * que más veces nos costó hoy. Medido sobre una foto real: "nacimiento" salió
 * "naciment", "birth" salió "birt", "vencimiento" salió "venciment", "Fecha"
 * salió "Feeha" y "Nacionalidad" salió "Nacionaidad".
 *
 * Tres criterios, de más a menos estricto:
 *   - la palabra contiene el sinónimo (el caso normal);
 *   - el sinónimo contiene a la palabra, si la palabra tiene cuerpo ("BIRT");
 *   - distancia de edición dentro de un cuarto del largo del sinónimo.
 *
 * El difuso solo se aplica a sinónimos de cuatro letras o más: con "SEX" una
 * letra de tolerancia haría match con cualquier cosa.
 */
export function pareceRotulo(palabra: string, sinonimo: string): boolean {
  const w = palabra.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z]/g, "");
  if (w.length === 0) return false;
  if (w.includes(sinonimo)) return true;
  // PREFIJO, no substring: el OCR trunca por el final ("BIRT" por "BIRTH").
  // Con substring, "NAME" caía dentro de "SURNAME" y la fila de los nombres se
  // excluía a sí misma por parecerse a la del apellido.
  //
  // Y la truncación se come una letra o dos, no cuatro: sin ese límite,
  // "NACIONAL" —de "REGISTRO NACIONAL DE LAS PERSONAS", el encabezado de la
  // tarjeta— pasaba por "NACIONALIDAD" y el cartel caía arriba de todo.
  if (w.length >= 4 && w.length >= sinonimo.length - 2 && sinonimo.startsWith(w)) return true;
  if (sinonimo.length < 4) return false;
  return distancia(w, sinonimo, Math.max(1, Math.floor(sinonimo.length / 4))) <= Math.max(1, Math.floor(sinonimo.length / 4));
}

export interface DefinicionDeRotulo {
  /** Cualquiera de estas palabras, en la fila, identifica el rótulo. */
  claves: string[];
  /** Si la fila contiene alguna de estas, NO es este rótulo. */
  excluye?: string[];
}

/**
 * TABLA DE SINÓNIMOS de los rótulos, como datos.
 *
 * Hoy español e inglés, que es lo que imprime el DNI argentino. Un documento de
 * otro país es agregar sinónimos acá, no tocar la lógica. Las variantes que el
 * OCR inventa —"naciment", "Feeha"— NO van en la tabla: las cubre la tolerancia
 * de `pareceRotulo`, porque son infinitas.
 *
 * `excluye` existe por un caso concreto: "SURNAME" contiene "NAME", así que la
 * fila del apellido parecía la de los nombres.
 */
export const ROTULOS: Record<string, DefinicionDeRotulo> = {
  apellido: { claves: ["APELLIDO", "SURNAME"] },
  nombres: { claves: ["NOMBRE", "NAME"], excluye: ["APELLIDO", "SURNAME"] },
  documento: { claves: ["DOCUMENTO", "DOCUMENT"], excluye: ["TRAMITE"] },
  nacimiento: { claves: ["NACIMIENTO", "BIRTH"] },
  emision: { claves: ["EMISION", "ISSUE"] },
  vencimiento: { claves: ["VENCIMIENTO", "EXPIRY"] },
  sexo: { claves: ["SEXO", "SEX"] },
  nacionalidad: { claves: ["NACIONALIDAD", "NATIONALITY"] },
  tramite: { claves: ["TRAMITE"] },
};

/**
 * Encabezados del documento: NO son rótulos de campo.
 *
 * "DOCUMENTO NACIONAL DE IDENTIDAD" contiene "DOCUMENTO" y contiene "NACIONAL",
 * que es prefijo de "NACIONALIDAD". Sin esta lista, el título de la tarjeta se
 * reconocía como dos rótulos a la vez y se marcaba una nacionalidad donde no
 * hay ninguna, arriba de todo.
 *
 * Alcanza con que la fila contenga una de estas para descartarla: un rótulo de
 * campo nunca comparte renglón con el título.
 */
export const ENCABEZADOS = [
  "IDENTIDAD",
  "REPUBLICA",
  "MERCOSUR",
  "RENAPER",
  // Las otras dos líneas del encabezado. "REGISTRO NACIONAL DE LAS PERSONAS"
  // no contiene ninguna de las de arriba, así que sin esto se colaba.
  "REGISTRO",
  "PERSONAS",
  "MINISTERIO",
];

export interface Rotulo {
  fila: PalabraOcr[];
  /** La palabra clave: es la que define la COLUMNA donde mirar abajo. */
  ancla: PalabraOcr;
}

/**
 * Rótulos que cumplen una definición, uno por fila.
 *
 * El ancla es la palabra clave, no la primera de la fila. Cuando dos rótulos
 * comparten renglón —"Sexo / Sex   Nacionalidad / Nationality"— anclar en la
 * primera manda a buscar el valor de la nacionalidad debajo del sexo.
 */
/**
 * Desde la palabra clave, camina a la izquierda mientras el hueco sea chico.
 *
 * El valor se alinea con el PRINCIPIO del rótulo, no con su palabra clave. En
 * "Fecha de nacimiento" la clave puede ser "nacimiento" —o "birth", cuando el
 * OCR estropea la española: medido, leyó "nacimeent"— y el valor arranca bajo
 * "Fecha". Caminar a la izquierda encuentra ese principio, y el hueco grande es
 * lo que impide cruzar al rótulo vecino cuando dos comparten renglón.
 */
/** La palabra clave de OTRO rótulo es un borde: ahí termina lo que es de éste. */
export function esDeOtroRotulo(
  w: PalabraOcr,
  propias: string[],
  tabla: Record<string, DefinicionDeRotulo> = ROTULOS
): boolean {
  return Object.values(tabla).some((def) =>
    def.claves.some((c) => !propias.includes(c) && pareceRotulo(w.text, c))
  );
}

function principioDeLaFrase(
  fila: PalabraOcr[],
  clave: PalabraOcr,
  propias: string[] = [],
  tabla: Record<string, DefinicionDeRotulo> = ROTULOS
): PalabraOcr {
  const h = alto(clave);
  let inicio = fila.indexOf(clave);
  while (inicio > 0) {
    const anterior = fila[inicio - 1];
    // Sin este corte, en "Sexo / Sex   Nacionalidad / Nationality" la frase de
    // la nacionalidad se comía la del sexo y el valor se buscaba en la columna
    // equivocada.
    if (esDeOtroRotulo(anterior, propias, tabla)) break;
    if (fila[inicio].bbox.x0 - anterior.bbox.x1 > h * 1.5) break;
    inicio--;
  }
  // Lo que el OCR deja suelto al margen no es parte del rótulo. Se piden dos
  // letras: con cero pasaban anclas como "a" y "lh", que corren la columna.
  while (inicio < fila.indexOf(clave) && norm(fila[inicio].text).length < 2) inicio++;
  return fila[inicio];
}

/**
 * @param tabla qué rótulos existen en ESTA cara. Importa para saber dónde
 * empieza la frase: caminando a la izquierda, la clave de otro rótulo es el
 * borde. Con la tabla equivocada no hay borde y el ancla se corre — medido, en
 * "EXPEDIENTE: 920271970 DISPOSICIÓN: 38" el ancla de la disposición se iba
 * hasta "EXPEDIENTE" y devolvía el expediente como valor.
 */
export function buscarRotulos(
  palabras: PalabraOcr[],
  def: DefinicionDeRotulo,
  tabla: Record<string, DefinicionDeRotulo> = ROTULOS
): Rotulo[] {
  const salida: Rotulo[] = [];
  for (const fila of filas(palabras)) {
    if (ENCABEZADOS.some((e) => fila.some((w) => pareceRotulo(w.text, e)))) continue;
    if (def.excluye?.some((e) => fila.some((w) => pareceRotulo(w.text, e)))) continue;
    const clave = fila.find((w) => def.claves.some((c) => pareceRotulo(w.text, c)));
    if (clave) salida.push({ fila, ancla: principioDeLaFrase(fila, clave, def.claves, tabla) });
  }
  return salida;
}

// ---------------------------------------------------------------------------
// 4. Valores
// ---------------------------------------------------------------------------

export interface ValorUbicado {
  texto: string;
  palabras: PalabraOcr[];
  caja: Rectangulo;
}

/**
 * El valor que está debajo de un rótulo.
 *
 * Dos pasos, y la distinción entre ambos es lo que costó encontrar:
 *
 *  1. La PRIMERA palabra del valor se busca en la columna del ancla y tiene que
 *     cumplir `sirve`. `sirve` identifica el arranque del valor y nada más.
 *  2. Desde ahí el valor se extiende a la derecha por su propia fila, SIN pedir
 *     `sirve` y sin la restricción de columna. Usar `sirve` para filtrar todo
 *     rompía las fechas: "21 OCT/OCT 1963" quedaba en "21 1963" porque "OCT/OCT"
 *     no tiene dígitos. Y restringir la columna cortaba "RODOLFO MANUEL".
 *
 * El corte entre una columna y la siguiente es el hueco horizontal: palabras
 * pegadas se juntan, palabras separadas no.
 */
export function valorBajoDeRotulo(
  palabras: PalabraOcr[],
  def: DefinicionDeRotulo,
  sirve: (w: PalabraOcr) => boolean,
  maxPalabras = 1
): ValorUbicado | null {
  // Las palabras que viven en una fila de rótulos no pueden ser valores. Es una
  // regla estructural y por eso atrapa lo que la textual no: "Naconaldacy" está
  // tan estropeado que no se parece a "NACIONALIDAD" por distancia de edición,
  // pero está en la fila de los rótulos, y eso alcanza.
  const enFilaDeRotulo = new Set<PalabraOcr>();
  for (const otra of Object.values(ROTULOS)) {
    for (const r of buscarRotulos(palabras, otra)) for (const w of r.fila) enFilaDeRotulo.add(w);
  }

  for (const { ancla } of buscarRotulos(palabras, def)) {
    const h = alto(ancla);
    const debajo = palabras
      .filter((w) => !enFilaDeRotulo.has(w))
      .filter((w) => w.bbox.y0 >= ancla.bbox.y1 - h * 0.3)
      .filter((w) => w.bbox.y0 < ancla.bbox.y1 + h * 3)
      .sort((a, b) => a.bbox.y0 - b.bbox.y0 || a.bbox.x0 - b.bbox.x0);

    // La ventana de columna es asimétrica a propósito. El ancla es la palabra
    // CLAVE, que en un rótulo de varias palabras no es la primera: en "Fecha de
    // vencimiento" la clave es "vencimiento" y el valor arranca bajo "Fecha",
    // más a la izquierda. Tres alturas de holgura a la izquierda lo cubren sin
    // llegar a la columna vecina.
    const enColumna = debajo.filter(
      (w) => w.bbox.x0 < ancla.bbox.x1 + h && w.bbox.x1 > ancla.bbox.x0 - h * 3
    );
    const primera = enColumna.find(sirve);
    if (!primera) continue;

    const hv = alto(primera);
    const mismaFila = debajo
      .filter((w) => Math.abs(w.bbox.y0 - primera.bbox.y0) < hv * 0.6)
      .filter((w) => w.bbox.x0 >= primera.bbox.x0)
      .sort((a, b) => a.bbox.x0 - b.bbox.x0);

    const partes: PalabraOcr[] = [];
    for (const w of mismaFila) {
      if (partes.length >= maxPalabras) break;
      const anterior = partes[partes.length - 1];
      if (anterior && w.bbox.x0 - anterior.bbox.x1 > hv * 1.2) break;
      partes.push(w);
    }
    const texto = partes.map((w) => w.text.trim()).join(" ").trim();
    if (texto) return { texto, palabras: partes, caja: unir(partes) };
  }
  return null;
}

// ---------------------------------------------------------------------------
// 5. Fechas
// ---------------------------------------------------------------------------

const MESES: Record<string, string> = {
  ENE: "01", JAN: "01", FEB: "02", MAR: "03", ABR: "04", APR: "04",
  MAY: "05", JUN: "06", JUL: "07", AGO: "08", AUG: "08", SEP: "09",
  SET: "09", OCT: "10", NOV: "11", DIC: "12", DEC: "12",
};

function esFechaReal(anio: number, mes: number, dia: number): boolean {
  if (!anio || mes < 1 || mes > 12 || dia < 1) return false;
  return dia <= new Date(Date.UTC(anio, mes, 0)).getUTCDate();
}

/**
 * Fecha impresa en el frente a ISO.
 *
 * El DNI argentino la imprime "21 OCT/OCT 1963" y también "10.12.1983" según el
 * modelo. El OCR mete basura entre el día y el mes —medido, "14'NOV/"—, así que
 * entre los dos se tolera cualquier cosa que no sea letra ni dígito.
 */
export function fechaDelFrente(texto: string): string | null {
  const limpio = texto.toUpperCase().replace(/\s+/g, " ").trim();

  const conLetras = limpio.match(/(\d{1,2})[^A-Z0-9]{0,3}([A-Z]{3})[^0-9]*?(\d{4})/);
  if (conLetras && MESES[conLetras[2]]) {
    const anio = parseInt(conLetras[3], 10);
    const mes = parseInt(MESES[conLetras[2]], 10);
    const dia = parseInt(conLetras[1], 10);
    if (esFechaReal(anio, mes, dia))
      return `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
  }

  const conSeparadores = limpio.match(/(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})/);
  if (conSeparadores) {
    const anio = parseInt(conSeparadores[3], 10);
    const mes = parseInt(conSeparadores[2], 10);
    const dia = parseInt(conSeparadores[1], 10);
    if (esFechaReal(anio, mes, dia))
      return `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// 6. La fuente
// ---------------------------------------------------------------------------

/**
 * ¿Este texto es en realidad un rótulo?
 *
 * Un valor que se parece a una etiqueta no es un valor. Sin esto el lector
 * reporta basura con cara de dato: medido, en 2026-frente.jpg devolvía
 * nombres="Naconaldacy" y en la foto de wikipedia nacionalidad="MINISTERIO".
 * Peor que no leer es leer mal, porque nadie lo revisa.
 */
export function pareceUnRotulo(texto: string): boolean {
  const palabras = texto.split(/\s+/).filter(Boolean);
  return palabras.some((p) =>
    Object.values(ROTULOS).some((def) => def.claves.some((c) => pareceRotulo(p, c)))
  );
}

/**
 * Un nombre impreso: palabras de dos letras para arriba.
 *
 * El OCR deja letras sueltas alrededor de los valores —medido, el nombre del
 * frente 2026 salió "f “MARIANA FLORENCIA"— y esas letras no son parte del
 * nombre de nadie. Las partículas sí se quedan: "DE LA TORRE" tiene dos
 * palabras de dos letras y las dos son del apellido.
 */
function nombrePropio(t: string): string | null {
  const limpio = textoLimpio(t)
    .split(" ")
    .filter((p) => p.length >= 2)
    .join(" ");
  return limpio || null;
}

/** Qué campo lee cada rótulo, y cómo reconocer el arranque de su valor. */
const CAMPOS: {
  campo: string;
  rotulo: DefinicionDeRotulo;
  arranca: (w: PalabraOcr) => boolean;
  maxPalabras: number;
  valor?: (texto: string) => string | null;
}[] = [
  // El arranque se busca sobre el texto LIMPIO, no sobre el crudo. Pedirlo
  // crudo descartaba el valor por un signo que el OCR inventó: en el frente de
  // 2026 el nombre salió `“MARIANA`, la expresión lo rechazaba, y el lector
  // seguía bajando hasta el renglón de los rótulos siguientes y devolvía
  // "Naconaldacy" —la nacionalidad mal leída— como si fuera el nombre.
  { campo: "apellido", rotulo: ROTULOS.apellido, arranca: (w) => /^[A-Z]{2,}$/.test(textoLimpio(w.text)), maxPalabras: 3, valor: nombrePropio },
  { campo: "nombres", rotulo: ROTULOS.nombres, arranca: (w) => /^[A-Z]{2,}$/.test(textoLimpio(w.text)), maxPalabras: 3, valor: nombrePropio },
  { campo: "numeroDocumento", rotulo: ROTULOS.documento, arranca: (w) => /\d{2}/.test(w.text), maxPalabras: 2,
    // Un documento argentino tiene 7 u 8 dígitos. Sin este piso, de "HEE 04:"
    // salía un "4" con cara de número de documento.
    valor: (t) => {
      const d = t.replace(/\D/g, "").replace(/^0+/, "");
      return d.length >= 7 && d.length <= 8 ? d : null;
    } },
  { campo: "fechaNacimiento", rotulo: ROTULOS.nacimiento, arranca: (w) => /\d/.test(w.text), maxPalabras: 4, valor: fechaDelFrente },
  { campo: "fechaVencimiento", rotulo: ROTULOS.vencimiento, arranca: (w) => /\d/.test(w.text), maxPalabras: 4, valor: fechaDelFrente },
  { campo: "fechaEmision", rotulo: ROTULOS.emision, arranca: (w) => /\d/.test(w.text), maxPalabras: 4, valor: fechaDelFrente },
  { campo: "sexo", rotulo: ROTULOS.sexo, arranca: (w) => /^[MFX]$/.test(textoLimpio(w.text)), maxPalabras: 1,
    valor: (t) => textoLimpio(t) || null },
  { campo: "nacionalidad", rotulo: ROTULOS.nacionalidad, arranca: (w) => /^[A-Z]{5,}$/.test(textoLimpio(w.text)), maxPalabras: 1,
    valor: nombrePropio },
];

/** Todo lo que el frente dice, como hallazgos. */
export function hallazgosDelFrente(palabras: PalabraOcr[]): Hallazgo[] {
  const salida: Hallazgo[] = [];
  for (const c of CAMPOS) {
    const ubicado = valorBajoDeRotulo(palabras, c.rotulo, c.arranca, c.maxPalabras);
    if (!ubicado) continue;
    // Un rótulo leído como valor no es un valor. Pasa cuando el rótulo de
    // arriba se leyó y el valor no: la ventana de abajo alcanza la etiqueta
    // siguiente y la devuelve como si fuera el dato.
    if (pareceUnRotulo(ubicado.texto)) continue;
    const valor = c.valor ? c.valor(ubicado.texto) : ubicado.texto;
    if (!valor) continue;
    salida.push({
      campo: c.campo,
      valor,
      crudo: ubicado.texto,
      origen: "impreso",
      caja: ubicado.caja,
    });
  }
  return salida;
}

// ---------------------------------------------------------------------------
// 7. Adaptador al formulario
// ---------------------------------------------------------------------------

/**
 * Los hallazgos del frente en la forma que espera el formulario.
 *
 * Es un adaptador y nada más: la lectura vive arriba, en hallazgos. Existe
 * mientras el modal siga consumiendo campos sueltos en vez del JSON consolidado.
 */
export interface DatosDelFrente {
  apellido: string | null;
  nombres: string | null;
  numeroDocumento: string | null;
  fechaNacimiento: string | null;
  fechaVencimiento: string | null;
  sexo: "M" | "F" | "X" | null;
  nacionalidad: string | null;
}

export function datosDelFrente(palabras: PalabraOcr[]): DatosDelFrente {
  const porCampo = new Map(hallazgosDelFrente(palabras).map((h) => [h.campo, h.valor]));
  const sexo = porCampo.get("sexo");
  return {
    apellido: porCampo.get("apellido") ?? null,
    nombres: porCampo.get("nombres") ?? null,
    numeroDocumento: porCampo.get("numeroDocumento") ?? null,
    fechaNacimiento: porCampo.get("fechaNacimiento") ?? null,
    fechaVencimiento: porCampo.get("fechaVencimiento") ?? null,
    sexo: sexo === "M" || sexo === "F" || sexo === "X" ? sexo : null,
    nacionalidad: porCampo.get("nacionalidad") ?? null,
  };
}

// ---------------------------------------------------------------------------
// 8. Orientación
// ---------------------------------------------------------------------------

/**
 * Cuántos rótulos distintos se reconocen. Es el puntaje para elegir orientación.
 *
 * Una foto de documento girada 90° produce texto ilegible: el OCR devuelve
 * "RAL", "HUN", "iil". Ninguna heurística de forma la salva, y probar las cuatro
 * orientaciones y quedarse con la que más rótulos reconoce sí, porque los
 * rótulos son lo único que está garantizado en todo documento del mismo modelo.
 *
 * Vive acá y no en la capa de percepción a propósito: "qué tan bien salió esta
 * lectura" es una pregunta sobre el DOCUMENTO. La percepción solo gira píxeles.
 */
export function puntajeDeOrientacion(palabras: PalabraOcr[]): number {
  return Object.values(ROTULOS).filter((def) => buscarRotulos(palabras, def).length > 0).length;
}
